import { CommonModule } from '@angular/common';
import { Component, OnChanges, OnInit, SimpleChanges, computed, inject, input, output, signal } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { RoleApi, Role } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-role-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, Spinner, ResponsiveTable],
  templateUrl: './role-grid.html',
  styleUrl: './role-grid.scss',
})
export class RoleGrid implements OnInit, OnChanges {
  private readonly roleApi = inject(RoleApi);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly applicationIds = input<readonly number[] | null>(null);
  readonly readOnly = input<boolean>(false);
  readonly reloadVersion = input<number>(0);

  readonly detailRequested = output<Role>();
  readonly totalChanged = output<number>();

  readonly loading = signal(false);
  readonly roles = signal<readonly Role[]>([]);

  // Pagination (same conventions as applications-grid)
  readonly pageNumber = signal(1); // 1-based
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);

  readonly trackByRole = (_: number, r: Role) => r.id ?? `${r.application?.code ?? ''}-${r.code}`;

  readonly columns = computed<readonly Column<Role>[]>(() => [
    {
      id: 'code',
      header: this.translateService.instant('pages.roles.fields.code'),
      value: (r) => r.code,
      nowrap: true,
    },
    {
      id: 'description',
      header: this.translateService.instant('pages.roles.fields.description'),
      value: (r) => r.description,
    },
  ]);

  readonly actions = computed<readonly RowAction<Role>[]>(() =>
    this.readOnly()
      ? []
      : [
          {
            id: 'details',
            label: this.translateService.instant('pages.roles.actions.details'),
            icon: 'fa-solid fa-pen-to-square',
          },
        ]
  );

  ngOnInit(): void {
    void this.loadRoles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    const applicationIdsChange = changes['applicationIds'];
    const shouldReload = Boolean(reloadVersionChange && !reloadVersionChange.firstChange)
      || Boolean(applicationIdsChange && !applicationIdsChange.firstChange);

    if (!shouldReload) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadRoles();
  }

  onActionSelected(ev: { actionId: string; item: Role }): void {
    if (this.readOnly()) {
      return;
    }

    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }
    this.pageNumber.set(page);
    void this.loadRoles();
  }

  private async loadRoles(): Promise<void> {
    const applicationIds = this.applicationIds();
    if (applicationIds !== null && applicationIds.length === 0) {
      this.roles.set([]);
      this.totalItems.set(0);
      this.totalChanged.emit(0);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.roleApi.search({
          payload: this.buildSearchPayload(),
        })
      );

      this.roles.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
      this.totalChanged.emit(this.totalItems());
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.roles.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private buildSearchPayload(): Record<string, unknown> {
    const applicationIds = this.applicationIds();

    return {
      page: this.pageNumber() - 1,
      size: this.pageSize(),
      ...(applicationIds && applicationIds.length > 0
        ? {
            filters: [
              {
                field: 'application.id',
                value: applicationIds.length === 1 ? applicationIds[0] : applicationIds,
                comparative: applicationIds.length === 1 ? 'eq' : 'in',
              },
            ],
          }
        : {}),
    };
  }
}
